import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ReportHeader from "./ReportHeader";
import { adminServices } from "../../../services/AdminServices";

const PAGE_SIZE = 20;

const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};

function CustomPagination({ total, rowsPerPage, currentPage, onChangePage }) {
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div style={paginationStyles.wrapper}>
      <button
        type="button"
        aria-label="Previous page"
        style={{ ...paginationStyles.link, ...(!canPrev ? paginationStyles.linkDisabled : {}) }}
        onClick={() => canPrev && onChangePage(currentPage - 1)}
        disabled={!canPrev}
      >
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>...</span>
        ) : (
          <button
            key={p}
            type="button"
            style={{ ...paginationStyles.pageNum, ...(p === currentPage ? paginationStyles.pageNumActive : {}) }}
            onClick={() => onChangePage(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next page"
        style={{ ...paginationStyles.link, ...(!canNext ? paginationStyles.linkDisabled : {}) }}
        onClick={() => canNext && onChangePage(currentPage + 1)}
        disabled={!canNext}
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

const formatCurrency = (v) =>
  v != null && !isNaN(Number(v)) ? `PHP${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const DUMMY_MONTHS = [];

const TotalProfitReport = ({ onBack, dashboard, metrics, backButtonIcon, facilityId = null, dateFrom = null, dateTo = null }) => {
  const [apiRows, setApiRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    const fetchTotalProfit = async () => {
      setLoading(true);
      setError(null);
      try {
        let endpoint = "Dashboard/totalProfit";
        const params = [];
        if (facilityId !== null && facilityId !== undefined && String(facilityId) !== "") params.push(`facilityId=${encodeURIComponent(facilityId)}`);
        if (dateFrom !== null && dateFrom !== undefined && String(dateFrom) !== "") params.push(`dateFrom=${encodeURIComponent(dateFrom)}`);
        if (dateTo !== null && dateTo !== undefined && String(dateTo) !== "") params.push(`dateTo=${encodeURIComponent(dateTo)}`);
        if (params.length > 0) endpoint += `?${params.join("&")}`;

        const response = await adminServices.getData(endpoint);
        const data = response?.data?.data ?? response?.data ?? [];
        if (!cancelled) setApiRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching Total Profit:", err);
        if (!cancelled) {
          setError(err);
          setApiRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTotalProfit();

    return () => { cancelled = true; };
  }, [facilityId, dateFrom, dateTo]);

  useEffect(() => { setCurrentPage(1); }, [facilityId, dateFrom, dateTo]);

  const dashboardRaw = dashboard?.totalProfitDetails ?? dashboard?.totalProfitList ?? (Array.isArray(dashboard?.totalProfit) ? dashboard.totalProfit : []);
  const months = apiRows.length > 0 ? apiRows : (Array.isArray(dashboardRaw) ? dashboardRaw : DUMMY_MONTHS);
  const pagedMonths = months.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const revTotal = metrics?.totalProfitRevenue != null ? Number(metrics.totalProfitRevenue) : months.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
  const costTotal = metrics?.totalProfitCost != null ? Number(metrics.totalProfitCost) : months.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const profitTotal = metrics?.totalProfit != null ? Number(metrics.totalProfit) : months.reduce((s, r) => s + (Number(r.profit) || 0), 0);
  const marginPct = metrics?.averageMargin != null ? String(metrics.averageMargin) : (revTotal > 0 ? ((profitTotal / revTotal) * 100).toFixed(2) : "0.00");

  return (
    <div className="dashboard-content">
      <ReportHeader onBack={onBack} breadcrumbLabel="Total Profit Report" backButtonIcon={backButtonIcon} />
      <div style={{ backgroundColor: "#ffffff", borderRadius: 12, padding: 24, marginTop: 24, boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700, color: "#111827" }}>Total Profit Report</h1>
            <p style={{ margin: 0, marginTop: 4, fontSize: 13, color: "#6b7280" }}>Monthly profit analysis and margin tracking</p>
          </div>
          <div style={{ backgroundColor: "#BCE9FF", padding: "18px 20px", borderRadius: 8, minWidth: 180, textAlign: "right" }}>
            <div style={{ fontSize: 12, color: "#0369a1", marginBottom: 4 }}>Total Profit</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(profitTotal) ?? "PHP0.00"}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 160px", minWidth: 160, backgroundColor: "#dcfce7", padding: "18px 20px", borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: "#166534", marginBottom: 4 }}>Total Revenue</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#166534" }}>{formatCurrency(revTotal) ?? "PHP0.00"}</div>
          </div>
          <div style={{ flex: "1 1 160px", minWidth: 160, backgroundColor: "#fee2e2", padding: "18px 20px", borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 4 }}>Total Cost</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#b91c1c" }}>{formatCurrency(costTotal) ?? "PHP0.00"}</div>
          </div>
          <div style={{ flex: "1 1 160px", minWidth: 160, backgroundColor: "#BCE9FF", padding: "18px 20px", borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: "#0369a1", marginBottom: 4 }}>Average Margin</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{marginPct}%</div>
          </div>
        </div>
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table style={{ minWidth: 520, width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ backgroundColor: "#0F2A40", color: "#ffffff" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Month</th>
                <th style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>Revenue (PHP)</th>
                <th style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>Cost (PHP)</th>
                <th style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>Profit (PHP)</th>
              </tr>
            </thead>
            <tbody>
              {pagedMonths.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "10px 12px" }}>{row.month}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#166534" }}>{formatCurrency(row.revenue)}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#b91c1c" }}>{formatCurrency(row.cost)}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a" }}>{formatCurrency(row.profit)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #e5e7eb", fontWeight: 600 }}>
                <td style={{ padding: "12px" }}>Total:</td>
                <td style={{ padding: "12px", textAlign: "right", color: "#166534" }}>{formatCurrency(revTotal)}</td>
                <td style={{ padding: "12px", textAlign: "right", color: "#b91c1c" }}>{formatCurrency(costTotal)}</td>
                <td style={{ padding: "12px", textAlign: "right", color: "#0f172a" }}>{formatCurrency(profitTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {months.length > PAGE_SIZE && (
          <CustomPagination
            total={months.length}
            rowsPerPage={PAGE_SIZE}
            currentPage={currentPage}
            onChangePage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default TotalProfitReport;
